import { useState, useEffect } from 'react'
import api from '../../services/api'
import Modal from '../../components/common/Modal'
import ErrorMessage from '../../components/common/ErrorMessage'
import Loading from '../../components/common/Loading'
import MenuList from './components/MenuList'
import MenuForm from './components/MenuForm'
import './Menus.css'

export interface MenuItem {
  id: number
  label: string
  slug: string
  position: number
  is_visible: boolean
}

export interface MenuFormData {
  label: string
  is_visible: boolean
}

function Menus() {
  const [menus, setMenus] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [editingMenu, setEditingMenu] = useState<MenuItem | null>(null)

  useEffect(() => {
    loadMenus()
  }, [])

  const loadMenus = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api.get<MenuItem[]>('/menus')
      // Sort by position
      const sorted = data.sort((a, b) => a.position - b.position)
      setMenus(sorted)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (menu: MenuItem) => {
    setEditingMenu(menu)
    setIsFormModalOpen(true)
  }

  const handleFormSubmit = async (formData: MenuFormData) => {
    if (!editingMenu) return

    try {
      // Update the menu item
      const updatedMenu = {
        ...editingMenu,
        ...formData,
      }

      // Update local state
      const updatedMenus = menus.map(m =>
        m.id === editingMenu.id ? updatedMenu : m
      )
      setMenus(updatedMenus)

      // Save to API (batch update)
      await api.put('/menus', updatedMenus)

      setIsFormModalOpen(false)
      setEditingMenu(null)
    } catch (err) {
      throw err
    }
  }

  const handleToggleVisibility = async (menu: MenuItem) => {
    try {
      const updatedMenus = menus.map(m =>
        m.id === menu.id ? { ...m, is_visible: !m.is_visible } : m
      )
      setMenus(updatedMenus)

      // Save to API (batch update)
      await api.put('/menus', updatedMenus)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour')
      // Revert on error
      loadMenus()
    }
  }

  const handleReorder = async (reorderedMenus: MenuItem[]) => {
    try {
      setSaving(true)
      setError('')

      // Update positions
      const menusWithNewPositions = reorderedMenus.map((menu, index) => ({
        ...menu,
        position: index + 1,
      }))

      setMenus(menusWithNewPositions)

      // Save to API (batch update)
      await api.put('/menus', menusWithNewPositions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
      // Revert on error
      loadMenus()
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className="menus-management">
      <div className="menus-header">
        <div>
          <h1>Gestion des menus</h1>
          <p className="menus-subtitle">
            Configurez l'ordre et la visibilité des menus du site
          </p>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {saving && (
        <div className="saving-indicator">
          Sauvegarde en cours...
        </div>
      )}

      <MenuList
        menus={menus}
        onEdit={handleEdit}
        onToggleVisibility={handleToggleVisibility}
        onReorder={handleReorder}
      />

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title="Modifier le menu"
      >
        <MenuForm
          initialData={editingMenu || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormModalOpen(false)}
        />
      </Modal>
    </div>
  )
}

export default Menus
